/* eslint-disable */
import { Injectable } from '@angular/core'
import { catchError, distinctUntilChanged, map, shareReplay, skipWhile, tap } from 'rxjs/operators'
import { BehaviorSubject, Observable, of } from 'rxjs'
import {
  FALLBACK_SETTINGS,
  FALLBACK_NOTIFICATION_POPUP_SETTING,
  FALLBACK_NOTIFICATION_SOUND_SETTING,
} from 'src/environments/go-api-fallbacks'
import { environment } from 'src/environments/environment'

import { ApiService } from '@shared/services/api.service'
import { Setting, InputSetting, SettingName } from '@shared/models/setting.model'
import { Integration } from '@shared/models/integration.model'
import { withFeedback } from '@app/core/interceptors/feedback-context'
@Injectable({ providedIn: 'root' })
export class SettingsService {
  private isNotificationPopup = new BehaviorSubject<boolean>(false)
  readonly isNotificationPopup$ = this.isNotificationPopup.asObservable()

  private isNotificationSound = new BehaviorSubject<boolean>(false)
  readonly isNotificationSound$ = this.isNotificationSound.asObservable()

  private soundVolume = new BehaviorSubject<string>('Default')
  readonly soundVolume$ = this.soundVolume.asObservable()

  private soundVolumeSettingCache: { [userId: string]: Observable<Setting | null> } = {}

  private vehicleDisplaySetting = new BehaviorSubject<Setting[]>(null)
  readonly vehicleDisplaySetting$ = this.vehicleDisplaySetting.asObservable().pipe(
    skipWhile((setting) => !setting),
    distinctUntilChanged(),
  )

  get isBilling(): boolean {
    return !!this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === SettingName.Billing)?.value
  }

  get isXeroEnabled(): boolean {
    return !!this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === SettingName.XeroEnabled)?.value
  }

  get isStripeEnabled(): boolean {
    return !!this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === SettingName.Stripe)?.value
  }

  get areBillingFunctionalitiesEnabled(): boolean {
    return !!this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === SettingName.Billing)?.value
  }

  get isReportRoutesCorrectedOnly(): boolean {
    return !!this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === SettingName.ReportRoutesCorrectedOnly)?.value
  }

  get reportsOptions(): string[] {
    return this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === SettingName.ReportsOptions)?.value
  }

  get hasRoyHillMaps(): boolean {
    return this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === SettingName.RoyHillMaps)?.value
  }

  constructor(private apiService: ApiService) {}

  private settingsCache: { [teamId: string]: Observable<Setting[]> } = {}

  getSettings(teamId: number): Observable<Setting[]> {
    const id = String(teamId)
    if (!this.settingsCache[id]) {
      if (environment.fakeApis) {
        this.settingsCache[id] = of(FALLBACK_SETTINGS as unknown as Setting[]).pipe(shareReplay({ bufferSize: 1, refCount: false }))
      } else {
        this.settingsCache[id] = this.apiService.get(`/settings/team/${teamId}`).pipe(
          map((resp) => resp['data']),
          map((data) => data || FALLBACK_SETTINGS),
          catchError(() => of(FALLBACK_SETTINGS)),
          shareReplay({ bufferSize: 1, refCount: false }),
        )
      }
    }
    return this.settingsCache[id]
  }

  updateSettings(settings: InputSetting[], teamId: number, notForSet = false, withToast = false): Observable<Setting> {
    const options = withToast ? { context: withFeedback(null, { onError: false }) } : undefined
    return this.apiService.patch(`/settings/team/${teamId}`, settings, options).pipe(
      tap((settings) => {
        delete this.settingsCache[String(teamId)]
        !notForSet && this.setVehicleDisplaySetting(settings['data'])
      }),
    ) as any
  }

  getIntegrationList(): Observable<Integration[]> {
    return this.apiService.get('/integrations').pipe(map((resp) => resp['data']))
  }

  getUserNotificationPopupSetting(userId: string): Observable<Setting> {
    if (environment.fakeApis) {
      return of(FALLBACK_NOTIFICATION_POPUP_SETTING as unknown as Setting)
    }
    return this.apiService.get(`/settings/user/${userId}/notificationPopup`).pipe(
      map((resp) => resp['data']),
      map(this.toSetting),
    )
  }

  getUserNotificationSoundSetting(userId: string): Observable<Setting> {
    if (environment.fakeApis) {
      return of(FALLBACK_NOTIFICATION_SOUND_SETTING as unknown as Setting)
    }
    return this.apiService.get(`/settings/user/${userId}/notificationSound`).pipe(
      map((resp) => resp['data']),
      map(this.toSetting),
    )
  }

  private toSetting = (setting) => (Object.keys(setting).length ? setting : null)

  updateUserSettings(settings: Partial<Setting>[], userId: string): Observable<Setting[]> {
    return this.apiService.patch(`/settings/user/${userId}`, settings).pipe(
      map((resp) => resp['data']),
      tap(() => {
        const byName = (settingName: SettingName) => (setting: Setting) => settingName === setting.name

        const isNotificationPopupSetting = settings.find(byName(SettingName.NotificationPopup))
        const isNotificationSoundSetting = settings.find(byName(SettingName.NotificationSound))
        const soundVolumeSetting = settings.find(byName(SettingName.SoundVolume))

        const isNotificationPopup: boolean = isNotificationPopupSetting?.value
        const isNotificationSound: boolean = isNotificationSoundSetting?.value
        const soundVolume: string = String(soundVolumeSetting?.value)

        if (isNotificationPopupSetting) {
          this.isNotificationPopup.next(isNotificationPopup)
        }

        if (isNotificationSoundSetting) {
          this.isNotificationSound.next(isNotificationSound)
        }

        if (soundVolumeSetting) {
          this.soundVolume.next(soundVolume)
          this.clearSoundVolumeCache(userId) // 👈 Invalidate cache only if updated
        }
      }),
    )
  }

  getUserSoundVolumeSetting(userId: string): Observable<Setting | null> {
    const id = String(userId)

    if (!this.soundVolumeSettingCache[id]) {
      const observable$ = this.apiService.get(`/settings/user/${id}/soundVolume`).pipe(
        map((resp) => resp['data']),
        map(this.toSetting),
        shareReplay({ bufferSize: 1, refCount: false }),
      )
      this.soundVolumeSettingCache[id] = observable$
    }

    return this.soundVolumeSettingCache[id]
  }

  getDigitalFormSetting(): Observable<Setting> {
    return this.apiService.get('/settings/digitalForm').pipe(map((resp) => resp['data']))
  }

  setVehicleDisplaySetting(settings: Setting[]): void {
    this.vehicleDisplaySetting.next(settings)
  }

  // TODO: Replace getters with this method
  checkForSetting(settingName: SettingName): boolean {
    return !!this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === settingName)?.value
  }

  clearSoundVolumeCache(userId: string): void {
    delete this.soundVolumeSettingCache[userId]
  }

  get isBlockoutEnabled(): boolean {
    return !!this.vehicleDisplaySetting.getValue()?.find(({ name }: Setting) => name === SettingName.Blockout)?.value
  }
}
